import { cardField, cardSchema } from '@shared/card-schema'
import { DOMSerializer } from '@tiptap/pm/model'
import { yXmlFragmentToProseMirrorRootNode } from '@tiptap/y-tiptap'
import { clsx } from 'clsx'
import { useEffect, useRef } from 'react'
import { useBoardCtx } from '../context'
import { renderMath } from '../math'
import { boardHrefAt, useOpenBoardLink } from '../boardLinks'

/**
 * A card's text, read-only and live: rendered from its Y fragment through the
 * card schema, so it looks exactly as in the card. No editor is bound to it,
 * so showing a card elsewhere (an embed) can never write to it.
 */
export function CardText({ id, className }: { id: string; className?: string }) {
  const { client } = useBoardCtx()
  const openLink = useOpenBoardLink()
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const frag = client.doc.getXmlFragment(cardField(id))
    const serializer = DOMSerializer.fromSchema(cardSchema)
    const render = () => {
      const el = root.current
      if (!el) return
      if (!frag.length) {
        el.replaceChildren()
        return
      }
      const doc = yXmlFragmentToProseMirrorRootNode(frag, cardSchema)
      el.replaceChildren(serializer.serializeFragment(doc.content))
      // Formulas are drawn by the editor's node view; drawn here the same way.
      renderMath(el)
    }
    render()
    frag.observeDeep(render)
    return () => {
      frag.unobserveDeep(render)
    }
  }, [client, id])
  return (
    <div
      ref={root}
      className={clsx('card-prose', className)}
      onClick={(e) => {
        const href = boardHrefAt(e.target)
        if (!href) return
        e.preventDefault()
        e.stopPropagation()
        openLink(href)
      }}
    />
  )
}
